import { useId, useState, type DragEvent, type ReactNode } from 'react';
import { CheckCircle2, UploadCloud } from 'lucide-react';
import { cn } from '@pixlide/ui';

const sizeFormatter = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

function formatSize(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${sizeFormatter.format(bytes / 1024)} Ko`
    : `${sizeFormatter.format(bytes / 1024 / 1024)} Mo`;
}

/**
 * Zone de dépôt de fichier : cliquer ou glisser-déposer.
 *
 * Remplace le `<input type="file">` natif, qui s'affichait comme une simple ligne de texte
 * (« Choisir un fichier — Aucun fichier sélectionné ») : un champ obligatoire passait
 * inaperçu, et l'admin découvrait l'oubli au clic sur « Créer ».
 *
 * Même câblage d'accessibilité que `Field` : le vrai `<input>` reste dans le DOM, relié au
 * libellé (clic, clavier, lecteurs d'écran), seulement masqué visuellement ; l'erreur est
 * reliée par `aria-describedby` et annoncée par `role="alert"`. L'anneau de focus est porté
 * par le cadre, jamais supprimé.
 */
export function FileDropzone({
  label,
  hint,
  accept,
  file,
  error,
  required = false,
  disabled = false,
  compact = false,
  onFile,
  children,
}: {
  label: string;
  hint?: string | undefined;
  accept: string;
  file: File | null;
  error?: string | undefined;
  required?: boolean;
  disabled?: boolean;
  /** Variante sur une ligne, pour les panneaux d'une carte. */
  compact?: boolean;
  onFile: (file: File | null) => void;
  /** Contenu affiché sous la zone (résumé d'analyse, avertissements). */
  children?: ReactNode;
}) {
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const [isDragging, setIsDragging] = useState(false);

  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  const onDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);

    if (!disabled) {
      onFile(event.dataTransfer.files[0] ?? null);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={inputId}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        className={cn(
          'flex cursor-pointer items-center gap-3 rounded-xl border border-dashed bg-white transition',
          'focus-within:ring-2 focus-within:ring-ink-900',
          compact ? 'px-3 py-2' : 'px-4 py-4',
          error !== undefined
            ? 'border-danger-500'
            : file
              ? 'border-success-500/60'
              : isDragging
                ? 'border-ink-900 bg-ink-50'
                : 'border-ink-100 hover:border-ink-300',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        {file ? (
          <CheckCircle2 aria-hidden className={cn('shrink-0 text-success-500', compact ? 'size-4' : 'size-6')} />
        ) : (
          <UploadCloud aria-hidden className={cn('shrink-0 text-ink-300', compact ? 'size-4' : 'size-6')} />
        )}

        <span className="flex min-w-0 flex-col">
          <span className={cn('font-medium text-ink-900', compact ? 'text-xs' : 'text-sm')}>
            {label}
            {required ? (
              <span aria-hidden className="text-danger-500">
                {' *'}
              </span>
            ) : null}
          </span>
          <span className="truncate text-xs text-ink-500">
            {file ? `${file.name} · ${formatSize(file.size)}` : 'Cliquez ou glissez un fichier ici'}
          </span>
        </span>

        {file && !compact ? <span className="ml-auto shrink-0 text-xs text-ink-500">Remplacer</span> : null}

        <input
          id={inputId}
          type="file"
          accept={accept}
          disabled={disabled}
          required={required}
          aria-invalid={error !== undefined}
          aria-describedby={describedBy === '' ? undefined : describedBy}
          onChange={(event) => {
            onFile(event.target.files?.[0] ?? null);
            // Permet de resélectionner le même fichier après l'avoir corrigé.
            event.target.value = '';
          }}
          className="sr-only"
        />
      </label>

      {hint ? (
        <p id={hintId} className="text-xs text-ink-500">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} role="alert" className="text-xs font-medium text-danger-500">
          {error}
        </p>
      ) : null}

      {children}
    </div>
  );
}
